import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  Spinner,
  Row,
  Col,
  Container,
  Button,
  Modal,
  Form,
  Tooltip,
  OverlayTrigger,
} from "react-bootstrap";
import Message from "../components/Message";
import {
  templateDetails,
  templateDelete,
  templateUpdate,
  getFlavorInfo,
} from "../slices/templateSlice";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { FaTrash, FaEdit } from "react-icons/fa";

function TemplateDetailsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  let params = useParams();

  const {
    error,
    loading,
    template,
    flavor: flavorDetails,
  } = useSelector((state) => state.template);
  const { userInfo } = useSelector((state) => state.user);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const handleShowDeleteModal = () => setShowDeleteModal(true);
  const handleCloseDeleteModal = () => setShowDeleteModal(false);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editedTemplate, setEditedTemplate] = useState({ ...template });
  const handleShowEditModal = () => setShowEditModal(true);
  const handleCloseEditModal = () => setShowEditModal(false);

  const [hoveredFlavor, setHoveredFlavor] = useState(false);

  const extractId = (paramId) => {
    const stateIndex = paramId.indexOf("&state");
    if (stateIndex !== -1) {
      return paramId.slice(0, stateIndex);
    }
    return paramId;
  };

  useEffect(() => {
    const cleanedId = extractId(params.id);
    if (cleanedId) {
      dispatch(templateDetails(cleanedId));
    }
  }, [dispatch, params.id]);

  const handleDelete = () => {
    const cleanedId = extractId(params.id);
    dispatch(templateDelete(cleanedId));
    navigate("/");
    setShowDeleteModal(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedTemplate({
      ...editedTemplate,
      [name]: value,
    });
  };

  const handleSubmitEdit = () => {
    const cleanedId = extractId(params.id);
    dispatch(templateUpdate(cleanedId, editedTemplate));
    setShowEditModal(false);
  };

  const formatValue = (value) => {
    if (Array.isArray(value)) {
      return value.map((item) => formatValue(item)).join(", ");
    }

    if (typeof value === "object" && value !== null) {
      if ("key" in value && "value" in value) {
        return `${value.key}: ${formatValue(value.value)}`;
      }

      return Object.entries(value)
        .map(([k, v]) => `${k}: ${formatValue(v)}`)
        .join(", ");
    }

    return String(value ?? "-");
  };

  if (!template || Object.keys(template).length === 0) {
    return (
      <div className="main-container">
        <Header />
        <div
          style={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <p>
            <strong style={{ fontSize: "38px" }}>Template not found :(</strong>
          </p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="main-container">
      <Header />

      {/* wrapper to push footer to the bottom */}
      <div style={{ flex: 1, width: "100%", paddingBottom: "30px" }}>
        {loading ? (
          <span style={{ display: "flex", padding: "20px" }}>
            <h5>Getting Template Details</h5>
            <span className="ml-2">
              <Spinner animation="border" />
            </span>
          </span>
        ) : error ? (
          <div style={{ padding: "20px" }}>
            <Message variant="danger">{error}</Message>
          </div>
        ) : (
          <div>
            {/* 3-column flex layout */}
            <div
              className="d-flex flex-column flex-md-row align-items-center justify-content-between mb-4 mt-3"
              style={{ padding: "0 20px" }}
            >
              <div className="d-none d-md-block" style={{ flex: 1 }}></div>

              <div className="text-center" style={{ flex: 2 }}>
                <p className="m-0">
                  <strong
                    className="text-capitalize"
                    style={{ fontSize: "clamp(30px, 4vw, 38px)" }}
                  >
                    {template?.name || "Template Name"}
                  </strong>
                </p>
              </div>

              <div
                className="d-flex justify-content-center justify-content-md-end mt-3 mt-md-0"
                style={{ flex: 1, gap: "5px" }}
              >
                {userInfo?.isAdmin ? (
                  <>
                    {/* Edit Button */}
                    <OverlayTrigger
                      placement="bottom"
                      overlay={
                        <Tooltip id="tooltip-edit" className="custom-tooltip">
                          Edit Template
                        </Tooltip>
                      }
                    >
                      <Button variant="primary" onClick={handleShowEditModal}>
                        <FaEdit />
                      </Button>
                    </OverlayTrigger>

                    {/* Delete Button */}
                    <OverlayTrigger
                      placement="bottom"
                      overlay={
                        <Tooltip id="tooltip-delete" className="custom-tooltip">
                          Delete Template
                        </Tooltip>
                      }
                    >
                      <Button variant="danger" onClick={handleShowDeleteModal}>
                        <FaTrash />
                      </Button>
                    </OverlayTrigger>
                  </>
                ) : null}
              </div>
            </div>

            {/* Delete Template Modal */}
            <Modal show={showDeleteModal} onHide={handleCloseDeleteModal}>
              <Modal.Header closeButton>
                <Modal.Title>Delete Template</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <p>Are you sure you want to delete this template?</p>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={handleCloseDeleteModal}>
                  Cancel
                </Button>
                <Button variant="danger" onClick={handleDelete}>
                  Delete
                </Button>
              </Modal.Footer>
            </Modal>

            {/* Edit Template Modal */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
              <Modal.Header closeButton>
                <Modal.Title>Edit Template</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Form>
                  {/* dynamically generated fields */}
                  {/* {Object.keys(editedTemplate).length > 0 ? ( */}
                  {false ? (
                    Object.keys(editedTemplate).map((key) => {
                      // skip the fields we don't want to render
                      if (key === "id") {
                        return null;
                      }

                      const field = editedTemplate[key];

                      if (typeof field === "boolean") {
                        return (
                          <Form.Group controlId={`template-${key}`} key={key}>
                            <Form.Label>
                              {key.charAt(0).toUpperCase() + key.slice(1)}
                            </Form.Label>
                            <Form.Check
                              type="checkbox"
                              name={key}
                              // value={editedTemplate?.key}
                              checked={field || false}
                              onChange={(e) =>
                                setEditedTemplate({
                                  ...editedTemplate,
                                  [key]: e.target.checked,
                                })
                              }
                            />
                          </Form.Group>
                        );
                      }

                      if (
                        typeof field === "string" ||
                        typeof field === "number"
                      ) {
                        return (
                          <Form.Group controlId={`template-${key}`} key={key}>
                            <Form.Label>
                              {key.charAt(0).toUpperCase() + key.slice(1)}
                            </Form.Label>
                            <Form.Control
                              type={
                                typeof field === "number" ? "number" : "text"
                              }
                              name={key}
                              // value={editedTemplate?.key}
                              value={field || ""}
                              onChange={handleInputChange}
                            />
                          </Form.Group>
                        );
                      }
                      return null; //hmm
                    })
                  ) : (
                    <p>No template data available.</p>
                  )}
                </Form>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={handleCloseEditModal}>
                  Cancel
                </Button>

                {false && (
                  <Button variant="primary" onClick={handleSubmitEdit}>
                    Save Changes
                  </Button>
                )}
              </Modal.Footer>
            </Modal>

            <Container>
              <Row>
                <Col sm>
                  <div style={{ marginBottom: "20px" }}>
                    {Object.keys(template).length > 0 ? (
                      <>
                        {/* display ID */}
                        {template.id !== undefined && (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "left",
                              border: "1px solid",
                              borderColor: "#DAE3ED",
                              padding: "5px",
                              margin: "2px 0",
                              wordBreak: "break-word",
                            }}
                          >
                            <span>{"id"}:</span>&nbsp;
                            <span style={{ color: "#4974A5" }}>
                              {template.id !== undefined
                                ? String(template.id)
                                : "-"}
                            </span>
                          </div>
                        )}

                        {/* display the other fields */}
                        {Object.keys(template).map((key) => {
                          const value = template[key];

                          // skip already shown fields (id)
                          if (key === "id") return null;

                          // display flavor field
                          if (key === "flavorName") {
                            const isFlavorEmpty =
                              !flavorDetails ||
                              !value ||
                              value === "null" ||
                              value === "None" ||
                              Object.keys(flavorDetails).length === 0;

                            return (
                              <div
                                key={key}
                                style={{
                                  display: "flex",
                                  justifyContent: "left",
                                  border: "1px solid",
                                  borderColor: "#DAE3ED",
                                  padding: "5px",
                                  margin: "2px 0",
                                  wordBreak: "break-word",
                                }}
                                onMouseEnter={() => setHoveredFlavor(true)}
                                onMouseLeave={() => setHoveredFlavor(false)}
                              >
                                <span>{key}:</span>&nbsp;
                                <span style={{ color: "#4974A5" }}>
                                  {!value ||
                                  ["null", "none"].includes(
                                    value.toLowerCase().trim(),
                                  )
                                    ? "-"
                                    : value}
                                </span>
                                {hoveredFlavor && !isFlavorEmpty && (
                                  <div
                                    style={{
                                      padding: "10px",
                                      zIndex: 100,
                                      top: "20px",
                                      color: "#9E6482BF", // 9E2222BF
                                    }}
                                  >
                                    {Object.entries(flavorDetails || {})
                                      .filter(([key]) => key !== "blob")
                                      .map(([key, value]) => (
                                        <div key={key}>
                                          <strong>{key}:</strong>&nbsp;
                                          {formatValue(value)}
                                        </div>
                                      ))}
                                  </div>
                                )}
                              </div>
                            );
                          }

                          // display the other fields
                          return (
                            <div
                              key={key}
                              style={{
                                display: "flex",
                                justifyContent: "left",
                                border: "1px solid",
                                borderColor: "#DAE3ED",
                                padding: "5px",
                                margin: "2px 0",
                                wordBreak: "break-word",
                              }}
                            >
                              <span>{`${key}:`}</span>&nbsp;
                              <span
                                style={{
                                  color: "#4974A5",
                                  display: "block",
                                }}
                                dangerouslySetInnerHTML={{
                                  __html: formatValue(value).replace(
                                    /\n/g,
                                    "<br />",
                                  ),
                                }}
                              />
                            </div>
                          );
                        })}
                      </>
                    ) : (
                      <p>No details available for this template.</p>
                    )}
                  </div>
                </Col>
              </Row>
            </Container>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

export default TemplateDetailsPage;
